/**
 * Fog of war: which tiles a player sees right now (`computeVision`), from
 * their units, completed buildings, watch spots, disguised spies and
 * revealed attackers. Exploration and every fog-safe sim decision start here.
 */
import { axialHexDistance } from '../hexmap/coords';
import { getHexTileMapKey } from '../hexmap/model/keys';
import { hexesWithinRadius } from '../hexmap/model/map';
import type { HexTileMapKey } from '../hexmap/model/types';
import type { RuleSet } from '../rules/schema';
import { cityBonuses } from './bonuses';
import { buildingCells, buildingIndex } from './footprint';
import { buildingLevel, levelStat } from './levels';
import { territoryOwners } from './territory';
import type { Building, Cell, EntityId, PlayerId, SimState } from './types';

type TileVisibility = 'visible' | 'explored' | 'unexplored';

/**
 * Disc offsets by radius, `[dq0, dr0, dq1, dr1, …]` in `hexesWithinRadius`
 * order, so a disc adds its tiles in the same order as before.
 */
const discOffsets = new Map<number, number[]>();

function offsetsOf(radius: number): number[] {
  let offsets = discOffsets.get(radius);
  if (!offsets) {
    offsets = hexesWithinRadius({ q: 0, r: 0 }, radius).flatMap((c) => [c.q, c.r]);
    discOffsets.set(radius, offsets);
  }
  return offsets;
}

/** Interned tile keys, so the hot vision loops don't build a string per tile. */
const KEY_SPAN = 1 << 13;
const internedKeys = new Map<number, HexTileMapKey>();

function keyOf(q: number, r: number): HexTileMapKey {
  const half = KEY_SPAN / 2;
  if (!Number.isInteger(q) || !Number.isInteger(r) || Math.abs(q) >= half || Math.abs(r) >= half) {
    return getHexTileMapKey({ q, r });
  }
  const packed = (q + half) * KEY_SPAN + (r + half);
  let key = internedKeys.get(packed);
  if (key === undefined) {
    key = getHexTileMapKey({ q, r });
    internedKeys.set(packed, key);
  }
  return key;
}

/** Adds every tile within `radius` of `cell` to `result`. */
function addDisc(result: Set<HexTileMapKey>, cell: Cell, radius: number): void {
  const offsets = offsetsOf(radius);
  for (let i = 0; i < offsets.length; i += 2) {
    result.add(keyOf(cell.q + offsets[i], cell.r + offsets[i + 1]));
  }
}

/**
 * Returns the set of tile keys currently visible to `playerId`: the union of
 * `hexesWithinRadius(entity.pos, def.visionRadius)` over that player's OWN
 * units and OWN buildings.
 *
 * Divergence from {@link computeTerritory}: buildings contribute vision at ANY
 * `buildProgress` (a construction site still sees), whereas territory only
 * counts completed buildings. A building sees at least its territory radius at
 * its level, so sites placed anywhere in a grown city's land are seen (and
 * picked up by workers); a city also sees the `cityVision` of the completed
 * buildings in its territory (a watchtower), and a completed watchtower sees
 * `watchRadius` around its watch spot while the spot is within its
 * `watchRange` (`watchedTiles`). A spy disguised in a rival city (spies.ts)
 * also sees that city's whole territory. A rival unit or building that recently damaged
 * this player (`PlayerState.revealed`, see systems/combat.ts) adds only the
 * tile(s) it stands on (`revealedTiles`), so it can be seen and targeted
 * through fog without revealing anything around it; `reveals: false` leaves
 * them out (exploration and the client's tile memory, so a revealed tile is
 * never explored or remembered). Unknown unit/building types are skipped
 * defensively. Enemy entities contribute nothing. Read-only — never mutates
 * inputs; a fresh Set is returned.
 */
function computeVision(
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId,
  opts?: { reveals?: boolean }
): Set<HexTileMapKey> {
  const result = new Set<HexTileMapKey>();
  const infiltrated = new Set<EntityId>();

  for (const u of Object.values(state.units)) {
    if (u.owner !== playerId) continue;
    const def = rules.units[u.type];
    if (!def) continue;
    if (u.disguise) infiltrated.add(u.disguise.cityId);
    addDisc(result, u.pos, def.visionRadius);
  }

  const own = Object.values(state.buildings).filter((b) => b.owner === playerId);
  // Territory is only worked out when some own building widens a city's sight.
  const bonuses = own.some((b) => b.buildProgress >= 1 && givesCityVision(rules, b))
    ? cityBonuses(state, rules)
    : undefined;
  for (const b of own) {
    const def = rules.buildings[b.type];
    if (!def) continue;
    const level = buildingLevel(b);
    const radius =
      Math.max(
        levelStat(def, level, 'visionRadius') ?? def.visionRadius,
        levelStat(def, level, 'territoryRadius') ?? 0
      ) + (bonuses?.get(b.id)?.vision ?? 0);
    // A grown farm sees from each of its tiles.
    for (const cell of buildingCells(b)) addDisc(result, cell, radius);
  }

  for (const b of own) for (const key of watchedTiles(rules, b)) result.add(key);

  if (opts?.reveals !== false) for (const key of revealedTiles(state, playerId)) result.add(key);

  if (infiltrated.size > 0) {
    for (const [key, cityId] of territoryOwners(state, rules)) {
      if (infiltrated.has(cityId)) result.add(key);
    }
  }

  return result;
}

/** Whether `b`'s type widens its city's sight at `b`'s level. */
function givesCityVision(rules: RuleSet, b: Building): boolean {
  const def = rules.buildings[b.type];
  return !!def && (levelStat(def, buildingLevel(b), 'cityVision') ?? 0) > 0;
}

/** A building's watch reach and sight at its level; undefined if it can't watch. */
function watchStats(rules: RuleSet, b: Building): { range: number; radius: number } | undefined {
  const def = rules.buildings[b.type];
  if (!def) return undefined;
  const level = buildingLevel(b);
  const range = levelStat(def, level, 'watchRange');
  const radius = levelStat(def, level, 'watchRadius');
  return range === undefined || radius === undefined ? undefined : { range, radius };
}

/**
 * The tiles a watchtower sees around its watch spot (`Building.watch`): none
 * unless it's completed and the spot is within its current `watchRange`.
 */
function watchedTiles(rules: RuleSet, b: Building): HexTileMapKey[] {
  if (!b.watch || b.buildProgress < 1) return [];
  const stats = watchStats(rules, b);
  if (!stats || axialHexDistance(b.pos, b.watch) > stats.range) return [];
  return hexesWithinRadius(b.watch, stats.radius).map(getHexTileMapKey);
}

/**
 * The tiles `playerId` sees only because a rival there recently damaged them
 * (`PlayerState.revealed`): a revealed building's tiles, and a revealed unit's
 * tile while it's off every building (so an attacker passing over its own
 * side's buildings never exposes them) and not inside one.
 */
function revealedTiles(state: SimState, playerId: PlayerId): Set<HexTileMapKey> {
  const result = new Set<HexTileMapKey>();
  const revealed = state.players[playerId]?.revealed;
  if (!revealed) return result;
  let covered: Map<HexTileMapKey, unknown> | null = null;
  for (const [id, until] of Object.entries(revealed)) {
    if (until <= state.tick) continue;
    const unit = state.units[id];
    if (unit) {
      if (unit.inside) continue;
      const key = getHexTileMapKey(unit.pos);
      covered ??= buildingIndex(state);
      if (!covered.has(key)) result.add(key);
      continue;
    }
    const building = state.buildings[id];
    if (building) for (const cell of buildingCells(building)) result.add(getHexTileMapKey(cell));
  }
  return result;
}

export type { TileVisibility };
export { computeVision, revealedTiles, watchedTiles, watchStats };
